import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";

/**
 * Acesso — magic-link landing route.
 *
 * Flow: user clicks the CTA in an email → lands on
 * https://luxstaymadeira.pt/acesso?token=<raw>. We POST the token to
 * /auth/magic/consume, which:
 *   • validates it (single-use, hashed in DB, 30-min TTL)
 *   • sets httpOnly auth cookies on success
 *   • returns the target path embedded with the original token
 * On success we hard-navigate so the AuthContext picks up the fresh
 * cookies and the destination page renders signed-in. On failure
 * (invalid/expired/already-used) we redirect to
 * /login?redirect=<target_path> per spec.
 */
export default function Acesso() {
  const { company } = useCompany();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const [error, setError] = useState("");
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const token = params.get("token");
    if (!token) {
      navigate("/login?redirect=%2Fcliente%2Freservas", { replace: true });
      return;
    }
    api.post("/auth/magic/consume", { token })
      .then(async ({ data }) => {
        const target = data.redirect || "/cliente/reservas";
        try { await refreshMe?.(); } catch (_) { /* ignore */ }
        // Hard navigation so the SPA mounts the destination route with
        // the freshly-set cookies in scope.
        window.location.replace(target);
      })
      .catch(() => {
        // Per spec — fall back to login carrying the most likely intent.
        setError("Este link já foi usado ou expirou.");
        setTimeout(() => {
          navigate("/login?redirect=%2Fcliente%2Freservas", { replace: true });
        }, 1500);
      });
  }, [params, navigate, refreshMe]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#031c18] text-[#fdfbf7] px-6"
         data-testid="acesso-page">
      <div className="max-w-sm w-full text-center space-y-5">
        <div className="font-display text-3xl tracking-wide text-[#d4af37]">{company.trade_name}</div>
        {!error ? (
          <>
            <div className="flex items-center justify-center gap-3 text-[#a0b2af] text-sm">
              <Loader2 className="animate-spin" size={16} />
              A iniciar a sua sessão&hellip;
            </div>
            <div className="text-[11px] tracking-widest uppercase text-[#a0b2af]/70">
              Acesso seguro · token de uso único
            </div>
          </>
        ) : (
          <>
            <div className="text-[#fca5a5] text-sm" data-testid="acesso-error">{error}</div>
            <div className="text-[11px] text-[#a0b2af]">
              A reencaminhar para a página de início de sessão&hellip;
            </div>
          </>
        )}
      </div>
    </div>
  );
}
